import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react'
import { cn } from '@/utils/cn'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface Column<T> {
  key: keyof T
  header: string
  width?: string
  render?: (value: T[keyof T], item: T) => React.ReactNode
}

interface PaginatedTableProps<T> {
  columns: Column<T>[]
  data: T[]
  page: number
  pageSize: number
  totalCount: number
  totalPages: number
  pageSizeOptions?: number[]
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  isLoading?: boolean
  getRowKey: (item: T) => string
  emptyMessage?: string
  itemLabel?: string
  pageSizeLabel?: string
}

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25]

export function PaginatedTable<T>({
  columns,
  data,
  page,
  pageSize,
  totalCount,
  totalPages,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  getRowKey,
  emptyMessage = 'No data available',
  itemLabel = 'items',
  pageSizeLabel = 'Rows per page:',
}: PaginatedTableProps<T>) {
  const handlePageSizeChange = (value: string) => {
    const newPageSize = parseInt(value, 10)
    onPageSizeChange(newPageSize)
    onPageChange(1) // Reset to first page when changing page size
  }

  const handlePreviousPage = () => {
    onPageChange(Math.max(1, page - 1))
  }

  const handleNextPage = () => {
    if (page < totalPages) {
      onPageChange(page + 1)
    }
  }

  if (isLoading) {
    return (
      <div className="px-6 py-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2 text-[#556374]" />
        <span className="text-[14px] text-[#556374]">Loading data...</span>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="px-6 py-12 text-center">
        <span className="text-[14px] text-[#556374]">{emptyMessage}</span>
      </div>
    )
  }

  return (
    <>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F8F8F8] border-b border-[#E5E7EB]">
              {columns.map(column => (
                <th
                  key={String(column.key)}
                  className="px-6 py-3 text-left text-[12px] font-semibold text-[#556374] uppercase tracking-wide"
                  style={column.width ? { width: column.width } : undefined}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr
                key={getRowKey(item)}
                className={cn(
                  'border-b border-[#E5E7EB] last:border-b-0',
                  index % 2 === 1 && 'bg-[#FAFAFA]'
                )}
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={String(column.key)}
                    className={cn(
                      'px-6 py-4 text-[14px]',
                      colIndex === 0
                        ? 'text-[#1C1C1C] font-medium'
                        : 'text-[#556374]'
                    )}
                  >
                    {column.render
                      ? column.render(item[column.key], item)
                      : String(item[column.key] ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-[#E5E7EB] flex flex-col sm:flex-row items-center justify-between gap-4">
        {/* Rows per page */}
        <div className="flex items-center gap-2">
          <span className="text-[14px] text-[#556374]">{pageSizeLabel}</span>
          <Select
            value={pageSize.toString()}
            onValueChange={handlePageSizeChange}
          >
            <SelectTrigger className="w-[70px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map(size => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Page info and navigation */}
        <div className="flex items-center gap-4">
          <span className="text-[14px] text-[#556374]">
            Page {page} of {totalPages} ({totalCount} {itemLabel})
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePreviousPage}
              disabled={page === 1}
              className={cn(
                'p-1.5 rounded-md transition-colors cursor-pointer',
                page === 1
                  ? 'text-[#D6DAE1] cursor-not-allowed'
                  : 'text-[#556374] hover:bg-[#F8F8F8]'
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleNextPage}
              disabled={page >= totalPages}
              className={cn(
                'p-1.5 rounded-md transition-colors cursor-pointer',
                page >= totalPages
                  ? 'text-[#D6DAE1] cursor-not-allowed'
                  : 'text-[#556374] hover:bg-[#F8F8F8]'
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
